import type {
  InviteRedeemResult,
  PublicInviteStatus,
} from '../../../types/botTesterInvite';

// SMS Lead Bot — the PUBLIC tester-invite page's data access.
//
// ⚠️ PLAIN `fetch`, not the shared `api()` helper (the public-survey posture).
// That helper attaches a Firebase ID token and throws on a non-2xx; this page
// runs for someone with no account at all, and its refusals arrive as 200s with
// a reason that the page has to render rather than throw away.

const BASE = '/api/public/bot-test';

export async function fetchInviteStatus(token: string): Promise<PublicInviteStatus> {
  try {
    const res = await fetch(`${BASE}/${encodeURIComponent(token)}`);
    if (!res.ok) return { status: 'not_found' };
    return (await res.json()) as PublicInviteStatus;
  } catch {
    // ⚠️ A network failure is NOT reported as "that link is invalid" — telling
    // someone their link is dead when the truth is a dropped connection sends
    // them back to whoever invited them for a link that was fine all along.
    throw new Error('We could not reach the server. Check your connection and try again.');
  }
}

export async function redeemInvite(
  token: string,
  body: { name: string; phone: string; clientName: string | null },
): Promise<InviteRedeemResult> {
  const res = await fetch(`${BASE}/${encodeURIComponent(token)}/redeem`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (res.status === 429) {
    return {
      ok: false,
      reason: 'failed',
      detail: 'Too many attempts. Please wait a little while and try again.',
    };
  }

  const payload = (await res.json().catch(() => null)) as InviteRedeemResult | null;
  if (payload && typeof payload === 'object' && 'ok' in payload) return payload;
  return { ok: false, reason: 'failed', detail: 'Something went wrong. Please try again.' };
}
